/**
 * CampaignMetricsView — Phase E.4 outbound dashboard.
 *
 * Visualizes the campaign-level metrics returned by
 * `/api/crm/metrics/campaign`:
 *   - KPI tiles  (sent / delivered / opened / clicked / bounced)
 *   - Per-sequence + per-template breakdown tables
 *   - Cold-prospect funnel  (discovered → researched → promoted → converted)
 *   - Daily timeseries sparkline (last 14 days)
 *   - Suppression list health
 *
 * All numbers are computed server-side. This view is read-only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Loader2,
  RefreshCw,
  Send,
  Eye,
  MousePointerClick,
  AlertTriangle,
  ShieldX,
  TrendingUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const WINDOW_OPTS = [
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "60", label: "Last 60 days" },
  { value: "90", label: "Last 90 days" },
];

function pct(n) {
  if (typeof n !== "number" || Number.isNaN(n)) return "0%";
  return `${(n * 100).toFixed(1)}%`;
}

function Kpi({ icon: Icon, label, value, hint, tone = "default" }) {
  const toneCls =
    tone === "pass"
      ? "text-emerald-300"
      : tone === "warn"
      ? "text-amber-300"
      : tone === "fail"
      ? "text-red-300"
      : "text-white";
  return (
    <Card className="bg-zinc-950 border-zinc-800">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          <Icon className="h-3 w-3" />
          {label}
        </div>
        <div className={`text-2xl font-semibold mt-1 ${toneCls}`}>{value}</div>
        {hint ? (
          <div className="text-[10.5px] text-zinc-500 mt-0.5">{hint}</div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Sparkline({ series, valueKey = "sent", color = "#fbbf24" }) {
  // series: [{day:'2026-06-01', email:{sent:0,...}, prospects:{...}}, ...]
  const points = useMemo(() => {
    if (!Array.isArray(series) || series.length === 0) return [];
    return series.map((d) => ({
      day: d.day,
      v: (d.email?.[valueKey] ?? d.prospects?.[valueKey]) || 0,
    }));
  }, [series, valueKey]);
  const max = Math.max(1, ...points.map((p) => p.v));
  const W = 360;
  const H = 60;
  const stepX = points.length > 1 ? W / (points.length - 1) : W;
  const path = points
    .map((p, i) => {
      const x = (i * stepX).toFixed(1);
      const y = (H - (p.v / max) * (H - 6) - 3).toFixed(1);
      return `${i === 0 ? "M" : "L"}${x},${y}`;
    })
    .join(" ");

  if (points.length === 0) {
    return <div className="text-zinc-600 text-[11px] py-2">No data</div>;
  }
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[60px]" preserveAspectRatio="none">
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
      {points.map((p, i) => {
        const x = (i * stepX).toFixed(1);
        const y = (H - (p.v / max) * (H - 6) - 3).toFixed(1);
        return (
          <circle key={i} cx={x} cy={y} r="1.6" fill={color} opacity={0.85}>
            <title>{`${p.day}: ${p.v}`}</title>
          </circle>
        );
      })}
    </svg>
  );
}

function RatesBar({ label, n, total, color = "bg-amber-500" }) {
  const pctVal = total > 0 ? Math.min(100, (n / total) * 100) : 0;
  return (
    <div className="text-[11.5px]">
      <div className="flex items-center justify-between">
        <span className="text-zinc-400">{label}</span>
        <span className="text-zinc-300">
          {n}/{total} <span className="text-zinc-500">({pctVal.toFixed(1)}%)</span>
        </span>
      </div>
      <div className="mt-1 h-1.5 bg-zinc-900 rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pctVal}%` }} />
      </div>
    </div>
  );
}

function FunnelRow({ step, count, prev }) {
  const conv = prev && prev > 0 ? (count / prev) * 100 : null;
  return (
    <div className="flex items-center gap-3 text-[12.5px] py-1.5">
      <div className="w-32 text-zinc-400 uppercase tracking-[0.12em] text-[10.5px]">{step}</div>
      <div className="flex-1 h-2 bg-zinc-900 rounded overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-500 to-amber-400"
          style={{
            width: prev && prev > 0 ? `${Math.min(100, (count / prev) * 100)}%` : count > 0 ? "100%" : "0%",
          }}
        />
      </div>
      <div className="text-white font-semibold w-16 text-right">{count}</div>
      <div className="w-16 text-right text-zinc-500">{conv === null ? "—" : `${conv.toFixed(0)}%`}</div>
    </div>
  );
}

export default function CampaignMetricsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState("30");
  const [seriesDays, setSeriesDays] = useState("14");

  const reload = useCallback(
    async (refreshOnly = false) => {
      if (refreshOnly) setRefreshing(true);
      else setLoading(true);
      try {
        const d = await crmApi.campaignMetrics({ days, series_days: seriesDays });
        setData(d);
      } catch (err) {
        toast.error(err.message || "Failed to load metrics");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [days, seriesDays],
  );

  useEffect(() => {
    reload();
  }, [reload]);

  const email = data?.email || {};
  const totals = email.totals || {};
  const rates = email.rates || {};
  const funnel = data?.funnel || {};
  const series = data?.series?.series || [];
  const suppression = data?.suppression || {};

  return (
    <div className="space-y-6" data-testid="owner-campaign-metrics-view">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-white">
            <BarChart3 className="h-5 w-5 text-amber-400" />
            Campaign Metrics
          </h1>
          <p className="text-sm text-zinc-400">
            Outbound deliverability + prospect funnel performance for the configured time window.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-9 w-[160px] bg-zinc-950 border-zinc-800 text-white" data-testid="metrics-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOW_OPTS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={seriesDays} onValueChange={setSeriesDays}>
            <SelectTrigger className="h-9 w-[150px] bg-zinc-950 border-zinc-800 text-white" data-testid="metrics-series-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Chart: 7d</SelectItem>
              <SelectItem value="14">Chart: 14d</SelectItem>
              <SelectItem value="30">Chart: 30d</SelectItem>
              <SelectItem value="60">Chart: 60d</SelectItem>
              <SelectItem value="90">Chart: 90d</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={() => reload(true)}
            disabled={refreshing}
            className="border-zinc-800 text-white hover:bg-zinc-900"
            data-testid="metrics-refresh"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {loading && (
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-10 flex items-center gap-2 text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading metrics…
          </CardContent>
        </Card>
      )}

      {!loading && data && (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi icon={Send} label="Sent / Delivered" value={`${totals.sent || 0} / ${totals.delivered || 0}`} hint={`delivery ${pct(rates.delivery)}`} />
            <Kpi icon={Eye} label="Opened" value={totals.opened || 0} hint={`${pct(rates.open)} of sends`} tone="pass" />
            <Kpi icon={MousePointerClick} label="Clicked" value={totals.clicked || 0} hint={`${pct(rates.click)} CTR`} tone="pass" />
            <Kpi icon={AlertTriangle} label="Bounced" value={totals.bounced || 0} hint={`${pct(rates.bounce)} bounce rate`} tone={totals.bounced ? "warn" : "default"} />
            <Kpi icon={ShieldX} label="Suppressed" value={suppression.total_all_time || 0} hint={`+${suppression.recent_total || 0} in window`} tone={suppression.recent_total ? "warn" : "default"} />
            <Kpi icon={TrendingUp} label="Active Sequences" value={(email.by_sequence || []).length} hint={`${(email.by_template || []).length} templates active`} />
          </div>

          {/* Rates panel */}
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-4 space-y-3">
              <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                Deliverability rates
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <RatesBar label="Delivery" n={totals.delivered || 0} total={(totals.sent || 0) + (totals.delivered || 0) + (totals.bounced || 0) + (totals.errored || 0)} color="bg-emerald-500" />
                <RatesBar label="Open" n={totals.opened || 0} total={(totals.delivered || 0) + (totals.sent || 0) || 1} color="bg-amber-500" />
                <RatesBar label="Click" n={totals.clicked || 0} total={(totals.delivered || 0) + (totals.sent || 0) || 1} color="bg-violet-500" />
                <RatesBar label="Bounce" n={totals.bounced || 0} total={(totals.sent || 0) + (totals.delivered || 0) + (totals.bounced || 0) + (totals.errored || 0)} color="bg-red-500" />
              </div>
            </CardContent>
          </Card>

          {/* Trend chart */}
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                  Daily trend ({series.length} days)
                </div>
                <div className="flex gap-3 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-400" /> Sends</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-400" /> Opens</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-violet-400" /> Clicks</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-400" /> Discovered</span>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Sends</div>
                  <Sparkline series={series} valueKey="sent" color="#fbbf24" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Opens</div>
                  <Sparkline series={series} valueKey="opened" color="#34d399" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Clicks</div>
                  <Sparkline series={series} valueKey="clicked" color="#a78bfa" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Prospects discovered</div>
                  <Sparkline series={series} valueKey="discovered" color="#60a5fa" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Funnel */}
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-4 space-y-2">
              <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                Cold prospect funnel — {funnel.total ?? 0} total
              </div>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.12em] text-zinc-600 pb-1 border-b border-zinc-900">
                <div className="w-32">Step</div>
                <div className="flex-1" />
                <div className="w-16 text-right">Count</div>
                <div className="w-16 text-right">Conv</div>
              </div>
              {(funnel.funnel || []).map((step) => {
                // step.step_conv may be null at the head
                const prev = step.step_conv != null && step.count > 0 ? step.count / step.step_conv : null;
                return (
                  <FunnelRow
                    key={step.status}
                    step={step.status}
                    count={step.count}
                    prev={prev ?? step.count}
                  />
                );
              })}
              {(funnel.by_source || []).length > 0 ? (
                <div className="pt-3 mt-2 border-t border-zinc-900">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">
                    By discovery source
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 text-[11.5px]">
                    {funnel.by_source.map((s) => (
                      <div key={s.source} className="rounded border border-zinc-800 bg-black p-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-white">{s.source}</span>
                          <Badge variant="outline" className="border-zinc-700 text-zinc-300 text-[10px]">
                            {s.count}
                          </Badge>
                        </div>
                        <div className="text-[10.5px] text-zinc-500 mt-1">
                          researched {s.researched} · promoted {s.promoted} · converted {s.converted}
                          <span className="text-amber-300 ml-2">
                            promote {(s.promote_rate * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* By sequence */}
          {(email.by_sequence || []).length > 0 && (
            <Card className="bg-zinc-950 border-zinc-800">
              <CardContent className="p-4 space-y-2">
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                  Performance by sequence
                </div>
                <div className="text-[12px]">
                  <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 border-b border-zinc-900">
                    <div className="col-span-4">Sequence</div>
                    <div className="col-span-1 text-right">Sent</div>
                    <div className="col-span-1 text-right">Open</div>
                    <div className="col-span-1 text-right">Click</div>
                    <div className="col-span-1 text-right">Bounce</div>
                    <div className="col-span-1 text-right">Open %</div>
                    <div className="col-span-1 text-right">CTR</div>
                    <div className="col-span-1 text-right">Bnc %</div>
                    <div className="col-span-1 text-right">DryRun</div>
                  </div>
                  {email.by_sequence.map((row) => (
                    <div key={row.sequence_slug} className="grid grid-cols-12 px-3 py-2 text-zinc-200 border-b border-zinc-900 last:border-b-0">
                      <div className="col-span-4 truncate text-white">{row.sequence_slug}</div>
                      <div className="col-span-1 text-right">{row.sent}</div>
                      <div className="col-span-1 text-right">{row.opened}</div>
                      <div className="col-span-1 text-right">{row.clicked}</div>
                      <div className="col-span-1 text-right text-amber-300">{row.bounced}</div>
                      <div className="col-span-1 text-right">{pct(row.open_rate)}</div>
                      <div className="col-span-1 text-right">{pct(row.click_rate)}</div>
                      <div className="col-span-1 text-right text-zinc-500">{pct(row.bounce_rate)}</div>
                      <div className="col-span-1 text-right text-zinc-500">{row.dry_run}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* By template */}
          {(email.by_template || []).length > 0 && (
            <Card className="bg-zinc-950 border-zinc-800">
              <CardContent className="p-4 space-y-2">
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                  Performance by template
                </div>
                <div className="text-[12px]">
                  <div className="grid grid-cols-12 px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 border-b border-zinc-900">
                    <div className="col-span-4">Template</div>
                    <div className="col-span-1 text-right">Sent</div>
                    <div className="col-span-1 text-right">Open</div>
                    <div className="col-span-1 text-right">Click</div>
                    <div className="col-span-1 text-right">Bounce</div>
                    <div className="col-span-1 text-right">Open %</div>
                    <div className="col-span-1 text-right">CTR</div>
                    <div className="col-span-2 text-right">All msgs</div>
                  </div>
                  {email.by_template.map((row) => (
                    <div key={row.template_slug} className="grid grid-cols-12 px-3 py-2 text-zinc-200 border-b border-zinc-900 last:border-b-0">
                      <div className="col-span-4 truncate text-white">{row.template_slug}</div>
                      <div className="col-span-1 text-right">{row.sent}</div>
                      <div className="col-span-1 text-right">{row.opened}</div>
                      <div className="col-span-1 text-right">{row.clicked}</div>
                      <div className="col-span-1 text-right text-amber-300">{row.bounced}</div>
                      <div className="col-span-1 text-right">{pct(row.open_rate)}</div>
                      <div className="col-span-1 text-right">{pct(row.click_rate)}</div>
                      <div className="col-span-2 text-right text-zinc-500">{row.all}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
